import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/feature/ScrollToTop';
import Breadcrumb from '@/components/base/Breadcrumb';
import SEOMeta from '@/components/base/SEOMeta';
import JsonLd from '@/components/base/JsonLd';
import { getArticleBySlug, getLatestArticles } from '@/mocks/articles';
import { categories } from '@/mocks/categories';
import ArticleCard from '@/components/feature/ArticleCard';
import NotFound from '@/pages/NotFound';

export default function ArticlePage() {
  const { slug } = useParams<{ slug: string }>();
  const article = slug ? getArticleBySlug(slug) : undefined;

  if (!article) {
    return <NotFound />;
  }

  const category = categories.find((c) => c.slug === article.category);
  const relatedArticles = getLatestArticles(4).filter((a) => a.slug !== article.slug).slice(0, 3);

  return (
    <>
      <ScrollToTop />
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={`逼特逼视频,${article.title},${category?.name || ''},逼特逼视频教程`}
        canonicalUrl={`https://www.bitbivideo.com/article/${article.slug}`}
        ogType="article"
      />
      <JsonLd type="Organization" />
      <JsonLd type="WebSite" />
      <JsonLd
        type="WebPage"
        pageTitle={article.title}
        pageDescription={article.excerpt}
        pageUrl={`https://www.bitbivideo.com/article/${article.slug}`}
      />
      <JsonLd
        type="BreadcrumbList"
        breadcrumbs={[
          { name: '首页', url: 'https://www.bitbivideo.com' },
          { name: category?.name || '内容分类', url: `https://www.bitbivideo.com/category/${article.category}` },
          { name: article.title, url: `https://www.bitbivideo.com/article/${article.slug}` },
        ]}
      />
      <JsonLd
        type="Article"
        pageUrl={`https://www.bitbivideo.com/article/${article.slug}`}
        articleData={{
          headline: article.title,
          description: article.excerpt,
          datePublished: article.date,
          author: article.author,
          url: `https://www.bitbivideo.com/article/${article.slug}`,
        }}
      />

      <Navbar />
      <Breadcrumb />

      <main>
        <article className="py-10 md:py-14 bg-background-50">
          <div className="max-w-4xl mx-auto px-4 md:px-6">
            <header className="mb-8">
              {category && (
                <Link
                  to={`/category/${category.slug}`}
                  className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-primary-100 text-xs font-medium text-primary-700 mb-4 whitespace-nowrap cursor-pointer hover:bg-primary-200 transition-colors duration-200"
                >
                  <i className={`${category.icon} text-xs`}></i>
                  {category.name}
                </Link>
              )}

              <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 leading-tight mb-4">
                {article.title}
              </h1>

              <div className="flex flex-wrap items-center gap-4 text-sm text-foreground-500">
                <span className="flex items-center gap-1">
                  <i className="ri-user-line"></i>
                  {article.author}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-calendar-line"></i>
                  {article.date}
                </span>
                <span className="flex items-center gap-1">
                  <i className="ri-time-line"></i>
                  阅读约 {article.readTime} 分钟
                </span>
              </div>
            </header>

            <div className="w-full h-64 md:h-96 rounded-xl overflow-hidden mb-10">
              <img
                src={article.imageUrl}
                alt={article.title}
                className="w-full h-full object-cover object-top"
                title={article.title}
              />
            </div>

            <div
              className="prose-custom"
              dangerouslySetInnerHTML={{ __html: article.content }}
            />

            <div className="mt-12 pt-8 border-t border-background-200/70">
              <div className="flex flex-wrap gap-2">
                <span className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600">
                  逼特逼视频
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600">
                  {category?.name}
                </span>
                <span className="px-3 py-1 rounded-full bg-background-100 text-xs text-foreground-600">
                  使用教程
                </span>
              </div>
            </div>
          </div>
        </article>

        {relatedArticles.length > 0 && (
          <section className="py-14 md:py-20 bg-background-100">
            <div className="max-w-7xl mx-auto px-4 md:px-6">
              <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-8">
                相关文章推荐
              </h2>
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-5 md:gap-6">
                {relatedArticles.map((ra) => (
                  <ArticleCard key={ra.slug} article={ra} />
                ))}
              </div>
            </div>
          </section>
        )}
      </main>

      <Footer />
    </>
  );
}